import { Flex, Tooltip, Typography } from '@aloft/toolkit-sui';

import { PropsWithChildren, useEffect, useRef } from 'react';

import {
  selectActiveTour,
  selectActiveTourIndex,
} from '../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { UIState } from '../../redux/types.ts';

type GuidedTourTooltipProps = {
  placement?: 'left' | 'right' | 'top' | 'bottom';
  text: string | string[];
  displayCondition?: boolean;
  tourID: UIState['activeTour'];
  tourIndex: number;
};

const ScrollWrapper = ({ children }: PropsWithChildren) => {
  const layoutRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    layoutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  return <span ref={layoutRef}>{children}</span>;
};

export const GuidedTourTooltip = ({
  children,
  placement = 'right',
  text,
  displayCondition = true,
  tourID,
  tourIndex,
}: PropsWithChildren<GuidedTourTooltipProps>) => {
  const activeTour = useAppSelector(selectActiveTour);
  const activeTourIndex = useAppSelector(selectActiveTourIndex);

  const isActiveTour = activeTour === tourID;
  const isActiveIndex = activeTourIndex === tourIndex;

  if (!displayCondition || !isActiveTour || !isActiveIndex) return children;

  return (
    <Tooltip
      pointerEvents="all"
      isOpen
      defaultIsOpen
      label={
        <Flex flexDirection="column" gap="1rem" padding=".5rem">
          {(typeof text === 'string' ? [text] : text).map((paragraph, idx) => (
            <Typography key={`${paragraph}-${idx}`} variant="body1" color="textOffset">
              {paragraph}
            </Typography>
          ))}
        </Flex>
      }
      placement={placement}
      hasArrow
      arrowSize={16}
    >
      <ScrollWrapper>{children}</ScrollWrapper>
    </Tooltip>
  );
};
